import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import Accordion from "../../components/v2/accordion/accordion";
import Link from "../../components/Link";
import CallToActionSmall from "../../components/v2/call-to-action-sm";
import { GetStaticProps } from "next";
import ComparisonForm from "../../components/v2/form/comparison-form";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { useState } from "react";
import "../../styles/scss/pricing.module.scss";
import '../../styles/scss/_faq-section.module.scss';
import "../../styles/scss/solutions.module.scss";
import TrustedCompaniesV2 from "../../components/v2/trusted-companies-v2";
import DatadogComparisonGrid from "../../components/v2/comparison/datadog-comparison-grid";
import DatadogComparisonTable from "../../components/v2/comparison/datadog-comparison-table";
import DatadogSwitchStats from "../../components/v2/comparison/datadog-switch-stats";
import DatadogPricingComparison from "../../components/v2/comparison/datadog-pricing-comparison";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import ComparisonVideoTestimonial from "../../components/v2/comparison/comparison-video-testimonial";
import { AIK8sRootIconDark, AutoFixIconDark, OpsAIDarkIcon, AIDashboardIconDark, AIAlertIconDark, ZeroReinstrumentationIcon, IncrementalAdoptionIcon, DataContinuityIcon } from "../../components/v2/icons-v2";

const Dialog = dynamic(() => import("../../components/v2/dialog"), {
  ssr: false,
});

export default function DatadogAlternative({ }) {
  const { t } = useTranslation('common');
  const data = t('comparison.datadog', { returnObjects: true, defaultValue: {} }) as any;
  const faqItems = Array.isArray(data?.faq?.questions) ? data.faq.questions : [];

  const [testimonialVideoOpen, setTestimonialVideoOpen] = useState(false);
  const [testimonialVideoSrc, setTestimonialVideoSrc] = useState<string | null>(null);

  const handleTestimonialVideoOpen = (src: string) => {
    setTestimonialVideoSrc(src);
    setTestimonialVideoOpen(true);
  };

  const formData = [
    {
      id: 1,
      header: "Zero Re-instrumentation",
      icon: <ZeroReinstrumentationIcon />,

    },
    {
      id: 2,
      header: "Incremental Adoption",
      icon: <IncrementalAdoptionIcon />,

    },
    {
      id: 3,
      header: "100% Data Continuity",
      icon: <DataContinuityIcon />,

    },
  ]

  const faqs = faqItems.length ? faqItems : [
    {
      id: 1,
      header: "What is the main difference between Middleware and Datadog's pricing models?",
      text: (
        <p>
          Middleware offers a transparent pricing model where you only pay for the data size, whereas Datadog uses a host/node-based pricing model.
        </p>
      ),
    },
    {
      id: 2,
      header:
        "Does Middleware offer a free trial?",
      text: (
        <p>
          Yes, Middleware offers a 14-day free trial with unlimited data ingestion across log monitoring, infrastructure monitoring, APM, and all platform features. No credit card is required to start. Once the trial ends, a payment method is required to continue ingesting new data.
        </p>
      ),
    },
    {
      id: 3,
      header:
        "How is Middleware's Pay-as-you-go plan better than Datadog's pricing?",
      text: (
        <p>
          At Middleware, you only pay for the services you use. All features are included, so you don't need to worry about selecting a plan for specific features. We charge for actual usage, not peak usage. This way, you can save up to 5X the cost with Middleware's pay-as-you-go plan compared to Datadog's pricing.
        </p>
      ),
    },
    {
      id: 4,
      header: "Can I subscribe only for one product (ex: log monitoring) and not for others (ex: APM)?",
      text: (
        <p>
          Yes. We have multiple products, and you can choose any or all of them. You will only be charged for the products to which you have subscribed.
        </p>
      ),
    },
    {
      id: 5,
      header: "How can I manage high log volume in a cost-effective way?",
      text: (
        <p>
          With our Ingestion Control feature and data pipeline, you can control what and how much data you want to ingest. You can also turn off non-relevant metrics for ingestion, which will reduce ingestion costs. To learn more about it, please book a demo with our Product expert.
        </p>
      ),
    },
    {
      id: 6,
      header: "Can I save money by switching from Datadog to Middleware?",
      text: (
        <p>
          Yes, Middleware offers customized pricing plans that can help you save up to 50% on your Datadog bill. Simply send us your Datadog invoice copy to get started.
        </p>
      ),
    },
  ];




  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Middleware Observability Platform",
    "url": "https://middleware.io/",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Web",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.7",
      "reviewCount": "320"
    }
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://middleware.io/comparison/datadog-alternative/",
    "name": "Middleware vs Datadog – Datadog Alternative",
    "description": "Compare Middleware and Datadog across observability features, pricing, and ease of use.",
    "mainEntity": {
      "@type": "ItemList",
      "itemListOrder": "Unordered",
      "numberOfItems": 2,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "SoftwareApplication",
            "name": "Middleware Observability Platform",
            "url": "https://middleware.io/",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "SoftwareApplication",
            "name": "Datadog",
            "url": "https://www.datadoghq.com/",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web"
          }
        }
      ]
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "url": "https://middleware.io/"
    }

  };


  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(softwareApplicationSchema)
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(webPageSchema)
          }}
        />
      </Head>
      <Layout
        isHomePage={false}
        pageTitle={
          "Datadog Alternative with Better Features, Lower Costs"
        }
        ogImage={"/images/comparison/datadog-og.webp"}
        uri={"/comparison/datadog-alternative/"}
        metaTitle={
          "Datadog Alternative with Better Features, Lower Costs"
        }
        metaDesc={
          "Compare Middleware vs Datadog and discover a powerful Datadog alternative with predictable pricing, OpenTelemetry-native observability, and AI insights."
        }
        metaRobotsNoindex={"index"}
        metaRobotsNofollow={"follow"}
      >
        <div className={"mw-vs-datadog solutions"}>
          <section className={`hero-section solutions-child-page`}>
            <div className={'container'}>
              <h1>The Better Datadog<span className='text-gradient'> Alternative</span><br /> Same Features, Better Experience</h1>
              <p className="mb-4">Tired of surprise invoices, host-based pricing, and complex agent deployments? Switch to Middleware, <br/> a unified, OpenTelemetry-native observability platform that delivers predictable pricing, one-minute setup, <br/> and complete visibility across your infrastructure, applications, and logs.</p>

              <div className={"d-flex"}>
                <Link href={"/demo-request/"} target={"_blank"}
                  className={"sign-up ahref-btn primary"}>Request a Demo</Link>
                <Link href={'#comparison-mw'} className={"sign-up ahref-btn secondary"}>See Comparison</Link>
              </div>
            </div>
          </section>


          <TrustedCompaniesV2
            className="home-page-customer"
            paragraph="Trusted By The Best Teams From"
          />

          <DatadogComparisonGrid />

          <CallToActionDocsLink
            title={'Point your Datadog agents at Middleware and switch incrementally'}
            docsLink="https://docs.middleware.io/third-party-agents/datadog-agent"
          />

          <section className="ai-native-features">
            <div className="container">
              <h2>The first observability platform built around AI, <br />
                not bolted onto it</h2>

              <section className={'mw-card career'}>
                <div className={'d-flex card-items'}>
                  <CardWithIcon
                    icon={<AIDashboardIconDark />}
                    title={"AI Dashboards"}
                    description={
                      "Describe what you want in plain English. Middleware builds the dashboard, selects the charts, and writes the queries. No PromQL, no custom query language, and no template gallery to comb through."
                    }
                  />
                  <CardWithIcon
                    icon={<AIAlertIconDark />}
                    title={"AI Alerts"}
                    description={
                      "Alerts that learn what 'normal' looks like for each service and surface only the deviations that matter. Anomaly detection that doesn't drown your on-call team in noise."
                    }
                  />
                  <CardWithIcon
                    icon={<OpsAIDarkIcon />}
                    title={"OpsAI · SRE Agent"}
                    description={
                      "An AI SRE that pinpoints root cause, identifies the offending line of code, and opens a pull request with the fix while you're still reading the alert. Auto-resolves issues with 80% accuracy."
                    }
                  />
                  <CardWithIcon
                    icon={<AIK8sRootIconDark />}
                    title={"AI K8s Root Cause Finder"}
                    description={
                      "Auto-structuring, severity detection, and pattern recognition built into log ingestion. No manual regular expressions, no parsing pipelines to maintain."
                    }
                  />
                  <CardWithIcon
                    icon={<AutoFixIconDark />}
                    title={"Auto-Fix · 60% Time Saved"}
                    description={
                      "Auto-fix issues across infrastructure and application layers with 80% accuracy. Engineers spend 60% less time resolving issues — and more time shipping products."
                    }
                  />

                </div>
              </section>
            </div>
          </section>

          <DatadogPricingComparison />

          <div className="customer-stories-comparison">
            <div className="container">
              <div className="section-tag testimonial">
                Testimonials
              </div>
              <h2><span className="text-gradient">Customer </span> Stories</h2>


              <div className="d-flex">
                <div className="customer-story-card">
                  <img src="/images/trusted-company/aceturtle.svg" alt="Aceturtle" />
                  <p>Middleware has played a very good role in transforming our observability and application performance. For instance, we reduced our total observability costs by 50%.</p>
                  <Link href="/customers/aceturtle/" className="read-more">
                    Read the story
                  </Link>
                  <div className="customer-author">
                    <p className="name">Vivek Gupta</p>
                    <p>Founder & CEO</p>
                  </div>
                </div>

                <div className="customer-story-card">
                  <img src="/images/trusted-company/hotplate.svg" alt="Hotplate" />
                  <p>We're heavy users of RUM and have tried many tools before discovering Middleware. We've been using OpsAI for the past few weeks, and it has automatically resolved about 60% of our production issues.</p>
                  <Link href="/customers/hotplate/" className="read-more">
                    Read the story
                  </Link>
                  <div className="customer-author">
                    <p className="name">Rangaraj Tirumala</p>
                    <p>Founding Engineer at Hotplate</p>
                  </div>
                </div>

                <ComparisonVideoTestimonial onVideoOpen={handleTestimonialVideoOpen} />
              </div>

            </div>
          </div>

          <DatadogComparisonTable />

          <DatadogSwitchStats />

          <ComparisonForm title={"You Don't Have to Rip Out Datadog on Day One"} label={"Datadog"} formId={"5ca644d9-8965-41c0-a38e-5586b9428ef8"} description={"Already running Datadog agents? Point them at Middleware and switch incrementally. No big-bang re-instrumentation required."} isDatadog={true} formDataComparison={formData} docsLink="https://docs.middleware.io/third-party-agents/datadog-agent" />

          <section className={"faq-section"}>
            <div className={"container"}>
              <h2>{String(data?.faq?.title ?? 'FAQs')}</h2>
              <p>{String(data?.faq?.subtitle ?? '')}</p>
              <div className={"faq-item"}>
                <Accordion faqs={faqs} showGroups={true} />
              </div>
            </div>
          </section>

          <CallToActionSmall />

        </div>
      </Layout>

      <Dialog
        isOpen={testimonialVideoOpen}
        position="center"
        onClose={() => {
          setTestimonialVideoOpen(false);
          setTestimonialVideoSrc(null);
        }}
        size="md"
        verticalCenter={true}
      >
        <div style={{ textAlign: "center" }}>
          {testimonialVideoSrc ? (
            <video
              key={testimonialVideoSrc}
              playsInline
              preload="none"
              autoPlay
              width="100%"
              height={450}
              controls={true}
            >
              <source src={testimonialVideoSrc} type="video/mp4" />
            </video>
          ) : null}
        </div>
      </Dialog>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translations = await serverSideTranslations(
    ctx?.locale ?? "en",
    ["common", "header", "footer"],
    nextI18NextConfig,
  );
  return {
    props: {
      ...translations,
      pageTitle: "Datadog Alternative | Middleware",
      description: "Ditch Datadog's costs without sacrificing performance. Middleware offers fast, intuitive, and affordable monitoring for apps, infrastructure, and logs.",
      ogImage: "/images/comparison/datadog-og.webp",
      uri: "/comparison/datadog-alternative/",
      metaTitle: "Datadog Alternative with Better Features, Lower Costs",
      metaDesc: "Compare Middleware vs Datadog and discover a powerful Datadog alternative with predictable pricing, OpenTelemetry-native observability, and AI insights.",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
    },
  }
}